import React, { useState, useEffect, useCallback, useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import EnhancedCoachManagementTable from './components/EnhancedCoachManagementTable';
import { MdDelete, MdEdit, MdAdd, MdError, MdMoreVert } from 'react-icons/md';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddCoachModal from "./components/AddCoachModal.jsx"
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import Loading from '../../../components/loading';
import TruncatedTeamsList from './components/TruncatedTeamsList.jsx';

const KebabMenu = ({ coachId, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const toggleMenu = (e) => {
    e.stopPropagation();
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleMenu}
        className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <MdMoreVert className="h-6 w-6" />
      </button>
      {isOpen && (
        <div className="absolute right-full top-0 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 mr-2">
          <div className="py-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(coachId);
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <MdEdit className="mr-2" /> Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(coachId);
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <MdDelete className="mr-2" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CoachManagement = () => {
  const [coachData, setCoachData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [coachToDelete, setCoachToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();

  const fetchData = async (url, options = {}) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.log('No token found, redirecting to sign-in');
      navigate('/auth/sign-in');
      return null;
    }

    const headers = new Headers(options.headers || {});
    headers.set('Authorization', `Token ${token}`);

    try {
      const response = await fetch(url, {
        ...options,
        method: options.method || 'GET',
        headers: headers,
        credentials: 'include',
        signal: AbortSignal.timeout(60000)
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          navigate('/auth/sign-in');
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 500) {
          throw new Error('Server error. The team is working on resolving this issue.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching data from ${url}:`, error);
      setError(error.message);
      toast.error(`Failed to fetch data: ${error.message}`);
      return null;
    }
  };

  const fetchCoachData = async () => {
    setIsLoading(true);
    const coachesData = await fetchData('https://staging-sit.com/api/coaches/');
    if (coachesData) {
      const coachesWithLoadingTeams = coachesData.map(coach => ({
        ...coach,
        teamsHandled: { isLoading: true, teams: [] }
      }));

      setCoachData(coachesWithLoadingTeams);
      setIsLoading(false);

      coachesWithLoadingTeams.forEach(coach => {
        fetchTeamsHandled(coach.id);
      });
    } else {
      setIsLoading(false);
    }
  };

  const fetchTeamsHandled = async (coachId) => {
    const data = await fetchData(`https://staging-sit.com/api/coach-team-management/${coachId}/teams_handled/`);
    if (data) {
      setCoachData(prevData => prevData.map(coach => 
        coach.id === coachId ? { ...coach, teamsHandled: { isLoading: false, teams: data.teams || [] } } : coach
      ));
    } else {
      setCoachData(prevData => prevData.map(coach => 
        coach.id === coachId ? { ...coach, teamsHandled: { isLoading: false, teams: [] } } : coach
      ));
    }
  };

  useEffect(() => {
    fetchCoachData();
  }, [retryCount]);

  const openDeleteModal = useCallback((id) => {
    setCoachToDelete(id);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setCoachToDelete(null);
    setIsDeleting(false);
  };

  const handleDelete = async () => {
    if (!coachToDelete) return;

    setIsDeleting(true);
    const result = await fetchData(`https://staging-sit.com/api/coaches/${coachToDelete}/`, { method: 'DELETE' });
    if (result !== null) {
      setCoachData(prevData => prevData.filter(coach => coach.id !== coachToDelete));
      toast.success("Coach deleted successfully");
      closeDeleteModal();
    }
    setIsDeleting(false);
  };

  const handleAddCoach = async (newCoachData) => {
    const userData = {
      username: newCoachData.email,
      email: newCoachData.email,
      password: newCoachData.password || 'defaultPassword123',
      fname: newCoachData.fname,
      lname: newCoachData.lname,
      user_type: 'coach',
    };

    const result = await fetchData('https://staging-sit.com/api/users/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });

    if (result) {
      console.log('Added coach data:', result);
      return result;
    } else {
      throw new Error('Failed to add coach');
    }
  };
  
  const handleRowClick = (rowData) => {
    console.log("Clicked row data:", rowData);
  };

  const handleEdit = useCallback((id) => {
    console.log(`Edit coach with id: ${id}`);
  }, []);

  const columnsDataColumnsCoaches = [
    {
      Header: "FIRST NAME",
      accessor: "fname",
    },
    {
      Header: "LAST NAME",
      accessor: "lname",
    },
    {
      Header: "EMAIL",
      accessor: "email",
    },
    {
      Header: "TEAMS HANDLED",
      accessor: "teamsHandled",
      Cell: ({ value }) => (
        <div>
          {value.isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            </div>
          ) : value.teams.length > 0 ? (
            <TruncatedTeamsList teams={value.teams} />
          ) : (
            <div className="flex items-center">
              <MdError style={{ color: 'orange' }} className="h-6 w-6 mr-1" />
              Unassigned
            </div>
          )}
        </div>
      ),
      className: 'teams-handled-column',
      minWidth: 150,
      maxWidth: 300,
    },
    {
      Header: "ACTIONS",
      accessor: "id",
      disableSortBy: true,
      Cell: ({ value }) => (
        <KebabMenu coachId={value} onEdit={handleEdit} onDelete={openDeleteModal} />
      ),
    },
  ];

  if (isLoading) return <Loading />;
  
  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden p-6">
        <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-4">Error Occurred</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">{error}</p>
        <div className="space-y-4">
          <button
            onClick={() => setRetryCount(prev => prev + 1)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
          >
            Retry
          </button>
          <button
            onClick={() => {
              localStorage.removeItem('authToken');
              localStorage.removeItem('user');
              navigate('/auth/sign-in');
            }}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
      <div className="p-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Coach Management</h2>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-[#f46524] text-white px-4 py-2 rounded hover:bg-[#f46570] dark:bg-[#7551FF] dark:hover:bg-[#9E8CFC] flex items-center"
        >
          <MdAdd className="mr-2" /> Add New Coach
        </button>
      </div>
      <div className="p-4">
        <EnhancedCoachManagementTable
          columnsData={columnsDataColumnsCoaches}
          tableData={coachData}
          onRowClick={handleRowClick}
        />
      </div>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
      <AddCoachModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddCoach}
        refreshCoachList={fetchCoachData}
      />
      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default CoachManagement;